import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { http, apiError, formatDateTime } from "../../lib/api";
import { Switch } from "../../components/ui/switch";
import { AutoStatusPill } from "./reviewAutoUi";

const cancel = (id) => http.post(`/admin/inquiries/${id}/review-request/cancel`);

export const ReviewAutomation = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["review-automation"], queryFn: async () => (await http.get("/admin/review-automation")).data });
  const items = Array.isArray(data?.items) ? data.items : [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["review-automation"] });
  const run = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      refresh();
    }
  };
  if (!data) return null;
  return (
    <section className="border hairline bg-[var(--ink-2)] p-5 mb-8" data-testid="review-automation">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <p className="eyebrow">Automatic review requests</p>
          <p className="text-sm text-slate-300 mt-2">When an inquiry is marked Completed, the customer is emailed a review link 3 days later, plus one gentle reminder 7 days after that if no review arrives. Sample reviews are never involved.</p>
        </div>
        <label className="flex items-center gap-3 text-sm text-slate-200">
          <Switch checked={!!data.enabled} onCheckedChange={(v) => run(() => http.put("/admin/review-automation", { enabled: v }), v ? "Automatic review requests turned on" : "Automatic review requests turned off")} data-testid="review-automation-toggle" />
          <span data-testid="review-automation-state">{data.enabled ? "On" : "Off"}</span>
        </label>
      </div>
      {!data.enabled && <p className="text-xs text-amber-300 mt-3">Automation is off: nothing is scheduled or sent automatically. Manual requests still work.</p>}
      {items.length > 0 ? (
        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-xs text-left min-w-[640px]" data-testid="review-automation-table">
            <thead className="text-slate-400 font-mono uppercase"><tr><th className="py-2 pr-3">Inquiry</th><th className="pr-3">Status</th><th className="pr-3">Initial request</th><th className="pr-3">Reminder</th><th /></tr></thead>
            <tbody className="divide-y divide-white/10 text-slate-300">
              {items.map((i) => {
                const a = i.review_auto || {};
                return (
                  <tr key={i.id} data-testid={`review-automation-row-${i.id}`}>
                    <td className="py-2 pr-3"><Link to={`/admin/inquiries?id=${i.id}`} className="text-gold hover:underline">{i.ref}</Link> · {i.name}<span className="block text-slate-400">{i.division || "No division"}</span></td>
                    <td className="pr-3"><AutoStatusPill status={a.status} testid={`review-automation-status-${i.id}`} />{a.status === "canceled" && <span className="block text-slate-400 mt-1">{a.cancel_reason}</span>}</td>
                    <td className="pr-3">{a.initial_sent_at ? `Sent ${formatDateTime(a.initial_sent_at)}` : a.status === "scheduled" ? `Scheduled ${formatDateTime(a.scheduled_for)}` : "—"}</td>
                    <td className="pr-3">{a.reminder_sent_at ? `Sent ${formatDateTime(a.reminder_sent_at)}` : a.status === "sent" && a.reminder_due ? `Due ${formatDateTime(a.reminder_due)}` : "—"}</td>
                    <td className="text-right">{["scheduled", "sent"].includes(a.status) && <button className="btn-ghost !py-1 !px-2 !text-[0.65rem]" onClick={() => run(() => cancel(i.id), "Canceled")} data-testid={`review-automation-cancel-${i.id}`}>Cancel</button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <p className="text-xs text-slate-400 mt-4">No automatic review requests yet. They appear here when inquiries are marked Completed.</p>}
    </section>
  );
};
